// useAutoSave.ts
import { useEffect, useRef, useCallback, useState } from "react";
import type {
    FieldValues,
    UseFormWatch,
    UseFormHandleSubmit,
    FieldErrors,
    SubmitHandler,
} from "react-hook-form";

type AutoSaveOpts<T extends FieldValues> = {
    watch: UseFormWatch<T>;
    handleSubmit: UseFormHandleSubmit<T>;
    isDirty: boolean;
    isValid: boolean;
    delay?: number;
    onSubmit: SubmitHandler<T>;
    canSubmit: boolean;
    errors?: FieldErrors<T>;
};

export default function useAutoSave<T extends FieldValues = FieldValues>({
                                                                             watch,
                                                                             handleSubmit,
                                                                             isDirty,
                                                                             isValid,
                                                                             delay = 600,
                                                                             onSubmit,
                                                                             canSubmit,
                                                                             errors,
                                                                         }: AutoSaveOpts<T>) {
    const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [pending, setPending] = useState(false);
    const isInitialMount = useRef(true);
    const lastSubmittedData = useRef<string | null>(null);
    // Traccia i campi con errori server per permettere il salvataggio dopo la modifica
    const serverErrorFieldsRef = useRef<Set<string>>(new Set());
    const prevErrorsSnapshot = useRef<string>('');

    // Aggiorna il tracking degli errori server
    useEffect(() => {
        const currentSnapshot = JSON.stringify(errors);
        if (currentSnapshot !== prevErrorsSnapshot.current) {
            const newServerErrors = new Set<string>();
            Object.entries(errors || {}).forEach(([key, val]) => {
                if ((val as any)?.type === "server") {
                    newServerErrors.add(key);
                }
            });
            serverErrorFieldsRef.current = newServerErrors;
            prevErrorsSnapshot.current = currentSnapshot;
        }
    }, [errors]);

    // Determina se il form è valido per l'auto-save
    // Permette il salvataggio anche se ci sono SOLO errori server (non errori di validazione)
    const hasOnlyServerErrors = useCallback(() => {
        if (!errors || Object.keys(errors).length === 0) return false;
        return Object.values(errors).every((e: any) => e?.type === "server");
    }, [errors]);

    // const isFormValidForAutoSave = isValid || hasOnlyServerErrors();
    const isFormValidForAutoSave = isValid ;

    const submit = useCallback(() => {
        if (pending) return;
        const run = handleSubmit(async (data: T) => {
            const current = JSON.stringify(data);
            // Permetti il submit anche se i dati sembrano uguali,
            // se l'utente ha modificato un campo con errore server
            const hasModifiedServerErrorField = Array.from(serverErrorFieldsRef.current).some(
                field => field in data
            );

            if (current === lastSubmittedData.current && !hasModifiedServerErrorField) {
                return;
            }

            try {
                setPending(true);
                await Promise.resolve(onSubmit(data));
                lastSubmittedData.current = current;
                // Pulisci il tracking degli errori server dopo un salvataggio riuscito
                serverErrorFieldsRef.current.clear();
            } catch (error) {
                // In caso di errore, mantieni il tracking per permettere un nuovo tentativo
                console.error("Auto-save failed:", error);
            } finally {
                setPending(false);
            }
        });
        return run();
    }, [handleSubmit, onSubmit, pending]);

    const kick = useCallback(() => {
        console.log('isDirty', isDirty, 'canSubmit', canSubmit, 'pending', pending, 'isFormValidForAutoSave', isFormValidForAutoSave, 'isValid', isValid, 'errors', errors);

        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            if (isDirty && canSubmit && isFormValidForAutoSave && !pending) {
                submit();
            }
        }, delay);
    }, [isDirty, canSubmit, isFormValidForAutoSave, pending, delay, submit]);

    useEffect(() => {
        const subscription = watch((_, { name }: { name?: string }) => {
            if (isInitialMount.current) {
                isInitialMount.current = false;
                return;
            }
            // Se l'utente ha modificato un campo con errore server, forza il reset del lastSubmittedData
            if (name && serverErrorFieldsRef.current.has(name)) {
                lastSubmittedData.current = null;
            }
            kick();
        });

        return () => {
            subscription?.unsubscribe?.();
            if (timer.current) clearTimeout(timer.current);
        };
    }, [watch, kick]);

    useEffect(() => {
        if (!isInitialMount.current && isDirty && canSubmit && isFormValidForAutoSave) {
            kick();
        }
    }, [isDirty, canSubmit, isFormValidForAutoSave, kick]);

    useEffect(() => () => {
        if (timer.current) clearTimeout(timer.current);
    }, []);

    useEffect(() => {
        if (!isDirty) {
            lastSubmittedData.current = null;
            // Pulisci anche il tracking degli errori server quando il form torna "pulito"
            serverErrorFieldsRef.current.clear();
        }
    }, [isDirty]);

    return kick;
}